<script>
	import { onMount } from 'svelte'
	import { slide } from 'svelte/transition'

	import Message from './Message.svelte'
	import LineItems from './LineItems.svelte'
	import CartTotals from './CartTotals.svelte'
	import { group_line_items } from './cart_functions'

	const api = make_api()

	let cart_loading
	let inited = false
	let apple_pay_inited = false
	let cart_message
	let coupon_message
	let giftcard_message
	let empty_cart
	let line_items = {}
	let removed_line_items = {}
	let coupons_enabled
	let subtotal
	let total
	let coupon_total
	let coupons
	let giftcard_payment
	let giftcards
	let shipping_price
	let tax_total
	let time
	let local_notices = []
	let wc_notices = []
	let apple_pay_errors = []
	let order_notes = ``
	let subscribe_to_primary
	let subscribe_to_politics
	let terms_and_conditions_accepted = false
	let tos_flash = false
	let terms_of_service_label

	let grouped_line_items = []
	let prev_item_keys = []

	function make_api() {
		const nonce_promise = fetch(`/wp-admin/admin-ajax.php?action=rest-nonce`)
			.then(res => res.text())
			.then(nonce => nonce.trim()) // NOTE: trim() is important here, removing will break prod!

		return async function api(route, data) {
			const nonce = await nonce_promise

			const cart_endpoint = nonce !== `0`
				? `/wp-json/cart${ route }?_wpnonce=${ nonce }`
				: `/wp-json/cart${ route }`

			return await fetch(cart_endpoint, {
				method: `POST`,
				headers: {
					Accept: `application/json`,
					"Content-Type": `application/json; charset=utf-8`,
				},
				body: JSON.stringify(data),
			})
		}
	}

	async function api_then_update_cart(route, data) {
		cart_loading = true

		const res = await api(route, data)

		const text = await res.text()
		try {
			// 500 responses can be parsed...
			const cart = JSON.parse(text)

			coupon_message = cart.coupon_message
			giftcard_message = cart.giftcard_message
			cart_message = cart.cart_message

			if (res.status < 200 || res.status >= 300) {
				const error = new Error(cart_message?.text || `http response status was ${ res.status } (${ route })`)
				error.status = res.status
				throw error
			}

			line_items = cart.line_items
			removed_line_items = cart.removed_line_items
			coupons_enabled = cart.coupons_enabled
			subtotal = cart.subtotal
			total = cart.total
			coupon_total = cart.coupon_total
			coupons = cart.coupons
			giftcard_payment = cart.giftcard_payment
			giftcards = cart.giftcards
			shipping_price = cart.shipping_price
			tax_total = cart.tax_total
			time = cart.time
			wc_notices = cart.wc_notices
			subscribe_to_primary = cart.subscribe_to_primary
			subscribe_to_politics = cart.subscribe_to_politics
			order_notes = cart.order_notes

			let items = {}

			for (let key of prev_item_keys) {
				if (removed_line_items[key]) {
					items[key] = removed_line_items[key]
				}
				if (line_items[key]) {
					items[key] = line_items[key]
				}
			}

			for (let key of Object.keys(line_items)) {
				if (!items[key]) {
					items[key] = line_items[key]
				}
			}

			prev_item_keys = Object.keys(items)
			grouped_line_items = group_line_items(items)
			empty_cart = !Object.keys(line_items).length
		} catch (e) {
			if (e.status === undefined || e.status === 500) {
				local_notices = [ ...local_notices, { type: `error`, text: e.message }]
			}
			console.error(e)
		} finally {
			if (window.wc_authorize_net_cim_credit_card_apple_pay_handler && window.ApplePaySession) {
				if (!apple_pay_inited) {
					window.wc_authorize_net_cim_credit_card_apple_pay_handler.ui_element = window.jQuery(`.cart`)
					window.wc_authorize_net_cim_credit_card_apple_pay_handler.block_ui = () => {
						cart_loading = true
					}
					window.wc_authorize_net_cim_credit_card_apple_pay_handler.unblock_ui = () => {
						cart_loading = false
					}
					window.wc_authorize_net_cim_credit_card_apple_pay_handler.render_errors = errors => {
						apple_pay_errors = errors.map(error => ({ type: `error`, text: error }))
					}
					window.wc_authorize_net_cim_credit_card_apple_pay_handler.init()
					apple_pay_inited = true
				}

				window.wc_authorize_net_cim_credit_card_apple_pay_handler.reset_payment_request()
			}
			cart_loading = false
		}
	}

	onMount(() => {
		api_then_update_cart(`/init`, {}).then(() => {
			inited = true
		})
	})

	function save_misc_data() {
		api(`/save_misc_data`, {
			subscribe_to_primary,
			subscribe_to_politics,
			order_notes,
		})
	}

	function require_terms_of_service() {
		if (!terms_and_conditions_accepted) {
			terms_of_service_label.scrollIntoView({ behavior: `smooth`, block: `nearest` })
			tos_flash = true
			setTimeout(() => {
				tos_flash = false
			}, 2000)
		}
	}

	$: notices = [ ...local_notices, ...wc_notices, ...apple_pay_errors ]
</script>

<div class="cart">
	{#if notices.length }
		<div class="notices" transition:slide|global>
			{#each notices as notice }
				{#if notice.html}
					<div class="notice {notice.type}">{@html notice.html}</div>
				{:else}
					<div class="notice {notice.type}">{notice.text}</div>
				{/if}
			{/each}
		</div>
	{/if}

	<div class="loading" hidden="{!cart_loading}"></div>
	<div class="disabled" hidden="{!cart_loading}"></div>

	{#if inited}

	{#if empty_cart}
		<div class="empty">Your cart is currently empty.</div>
	{:else}

		<LineItems
			api={api_then_update_cart}
			{time}
			grouped_line_items={grouped_line_items}
		/>

		<Message bind:message={cart_message} />

		<div class="review">
			<div class="totals">
				<div class="title">Totals</div>

				<CartTotals
					{subtotal}
					{shipping_price}
					{coupons_enabled}
					{coupons}
					{giftcards}
					{giftcard_payment}
					{total}
					api={api_then_update_cart}
					{coupon_total}
					{tax_total}
					{coupon_message}
					{giftcard_message}
				/>
			</div>

			<div class="misc">
				<div class="title">Personal</div>

				<div class="fields">
					<div>
						<label>
							<input type="checkbox" bind:checked={subscribe_to_primary} on:change={save_misc_data} />
							Subscribe to Newsletter
						</label>
						<small>T.REX ARMS will email you occasionally with product announcements, access to exclusive sales, videos, and educational articles. </small>
					</div>

					<div>
						<label>
							<input type="checkbox" bind:checked={subscribe_to_politics} on:change={save_misc_data} />
							Subscribe to Political Emails
						</label>
						<small>T.REX ARMS may email you occasionally with political information related to the 2nd Amendment -- legislation, action, or news relevant to your geographic location. </small>
					</div>

					<label class:flash={tos_flash}>
						<input type="checkbox" bind:checked={terms_and_conditions_accepted} on:change={save_misc_data} bind:this={terms_of_service_label} />
						I agree to the <a href="/terms-and-conditions" target="_blank">Terms and Conditions</a>
					</label>

					<div>
						<label for="order-notes">
							Order notes
						</label>
						<textarea id="order-notes" bind:value={order_notes} on:change={save_misc_data} style="width: 100%;" placeholder="Comments or requests, including upcoming class dates or deadlines."></textarea>
					</div>
				</div>
			</div>
		</div>

		<!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
		<div class="payment" on:click={require_terms_of_service}>
			<div class="title">Payment</div>

			{#if apple_pay_inited}
				<div class="sv-wc-apple-pay-button apple-pay-button-white" class:payment_disabled={!terms_and_conditions_accepted}></div>
			{/if}

			<a class="checkout" href="/checkout" class:payment_disabled={!terms_and_conditions_accepted} on:click={require_terms_of_service}>
				<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-credit-card"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect><line x1="1" y1="10" x2="23" y2="10"></line></svg>

				Credit Card
			</a>
		</div>

	{/if}
	{/if}
</div>

<style>
	.cart {
		position: relative;
		font-size: 16px;
		padding: 0 16px;
		min-height: 500px;
		margin-bottom: 20px;
	}

	@media (max-width: 600px) {
		.cart {
			font-size: 14px;
			padding: 0 5px;
		}
	}

	.empty {
		padding: 20px;
		text-align: center;
	}

	.disabled {
		position: absolute;
		top: 0;
		right: 0;
		bottom: 0;
		left: 0;
		z-index: 50;
		background: rgba(8, 8, 8, .6);
	}

	.loading {
		position: fixed;
		left: 0;
		right: 0;
		margin: 0 auto;
		top: 50%;
		margin-top: -50px;
		height: 100px;
		width: 100px;
		z-index: 51;
		background: #000 url('../../img/loading_100.gif');
		border-radius: 100%;
	}

	/* notices are similar to <Message />s */
	.notices {
		font-weight: 400 !important;
	}

	.notices .notice {
		padding: 10px 20px;
		font-size: 90%;
	}

	.notices .error {
		background: #a90707;
		color: #fff;
	}

	.notices .success {
		background: #ceff51;
		color: #000;
		border-radius: var(--default-border-radius);
	}

	.title {
		color: #666;
		font-size: 150%;
		text-transform: uppercase;
		font-weight: 600;
		letter-spacing: 1px;
		margin-bottom: 10px;
		line-height: 1;
	}

	.review {
		display: flex;
		flex-direction: row;
		margin-top: 20px;
	}

	.review .misc,
	.review .totals {
		width: 100%;
	}

	.review .misc {
		margin-left: 30px;
	}

	.review .misc .fields {
		display: flex;
		flex-direction: column;
		gap: 20px;
	}

	label {
		display: block;
		color: #fff;
		font-size: 16px;
		line-height: 2;
	}

	small {
		display: block;
		padding: 0 5px 0 27px;
		opacity: .8;
		line-height: 1.4;
	}

	@media (max-width: 700px) {
		.review {
			flex-direction: column;
			gap: 20px;
		}
		.review .misc,
		.review .totals {
			max-width: 500px;
			margin: 0 auto;
		}
	}

	.payment {
		display: flex;
		flex-direction: column;
		max-width: 500px;
		margin: 20px auto 0;
	}

	.checkout {
		display: block;
		color: #000;
		background: #ceff51;
		padding: 18px;
		border-radius: 5px; /* match apple pay button */
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 10px;
		font-size: 22px;
		line-height: 1;
	}

	.sv-wc-apple-pay-button {
		height: 60px; /* match credit card button */
	}

	.payment_disabled {
		pointer-events: none;
	}

	@keyframes flash {
		0%, 100% { outline: none; }
		50% { outline: 3px solid #fff; }
	}

	.flash {
		animation: flash 0.5s step-start 4;
	}
</style>
